import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { ordersApi, type OrdersListParams } from '../api';
import type {
  AdvanceTargetStatus,
  OrderResponse,
  OrderStatus,
  OrderTypeEnum,
  RejectPaymentAction,
} from '../types';
import { OrderActionError } from '../api';
import { rejectSuccessToastCopy } from '../utils/toastCopy';

export const ordersKeys = {
  /**
   * Prefix key for everything under this restaurant's owner orders cache.
   * Use for broad invalidation — matches `list`, `byStatus`, etc. via
   * react-query's hierarchical matching.
   */
  all: (restaurantId: string) => ['orders', restaurantId] as const,
  /** Narrow prefix that matches every list variant but skips byStatus / byType caches. */
  listPrefix: (restaurantId: string) => ['orders', restaurantId, 'list'] as const,
  /** All non-terminal, non-pending orders — independent of the date filter. */
  active: (restaurantId: string) => ['orders', restaurantId, 'active'] as const,
  /**
   * The list query keyed by the active date-range params (if any). Separate
   * from `all` so distinct date selections keep their own cache entries
   * without evicting each other.
   */
  list: (restaurantId: string, params?: OrdersListParams) =>
    ['orders', restaurantId, 'list', params ?? null] as const,
  byStatus: (restaurantId: string, status: OrderStatus) =>
    ['orders', restaurantId, status] as const,
  byType: (restaurantId: string, type: OrderTypeEnum) =>
    ['orders', restaurantId, 'type', type] as const,
  scheduled: (restaurantId: string) => ['orders', restaurantId, 'SCHEDULED'] as const,
  detail: (restaurantId: string, orderId: string) => ['order', restaurantId, orderId] as const,
};

export function useOrders(
  restaurantId: string | undefined,
  params?: OrdersListParams,
) {
  return useQuery({
    queryKey: restaurantId
      ? ordersKeys.list(restaurantId, params)
      : ['orders', 'disabled'],
    queryFn: () => ordersApi.getOrders(restaurantId!, params),
    enabled: !!restaurantId,
  });
}

export function usePendingOrders(restaurantId: string | undefined) {
  return useQuery({
    queryKey: restaurantId ? ordersKeys.byStatus(restaurantId, 'PENDING') : ['orders', 'disabled'],
    queryFn: () => ordersApi.getOrdersByStatus(restaurantId!, 'PENDING'),
    enabled: !!restaurantId,
  });
}

const ACTIVE_STATUSES_FOR_FETCH: OrderStatus[] = [
  'CONFIRMED',
  'PREPARING',
  'IN_DELIVERY',
  'READY_FOR_PICKUP',
];

/**
 * All non-terminal, non-pending orders for a restaurant, merged across
 * statuses and deduped by orderId. Independent of the date filter — carries
 * over any in-flight orders from previous days so they stay visible until
 * they terminate.
 */
export function useActiveOrders(restaurantId: string | undefined) {
  return useQuery({
    queryKey: restaurantId ? ordersKeys.active(restaurantId) : ['orders', 'disabled'],
    enabled: !!restaurantId,
    queryFn: async () => {
      if (!restaurantId) return [];
      const lists = await Promise.all(
        ACTIVE_STATUSES_FOR_FETCH.map((s) =>
          ordersApi.getOrdersByStatus(restaurantId, s),
        ),
      );
      const byId = new Map<string, OrderResponse>();
      for (const list of lists) {
        for (const o of list) byId.set(o.orderId, o);
      }
      return Array.from(byId.values());
    },
  });
}

export function useScheduledOrders(restaurantId: string | undefined) {
  return useQuery({
    queryKey: restaurantId ? ordersKeys.scheduled(restaurantId) : ['orders', 'disabled'],
    queryFn: () => ordersApi.getScheduledOrders(restaurantId!),
    enabled: !!restaurantId,
  });
}

export function useOrder(restaurantId: string | undefined, orderId: string | undefined) {
  return useQuery({
    queryKey:
      restaurantId && orderId ? ordersKeys.detail(restaurantId, orderId) : ['order', 'disabled'],
    queryFn: () => ordersApi.getOrder(restaurantId!, orderId!),
    enabled: !!restaurantId && !!orderId,
    retry: false,
  });
}

/**
 * Accept a pending order. Optimistically removes from the pending-list cache;
 * 409 Conflict (race with sweeper / other owner device) shows an info toast
 * and invalidates. `payment_capture_failed` is surfaced to the caller via
 * the rejected promise so per-row UI can render the recovery panel.
 */
export function useAcceptOrder(restaurantId: string | undefined) {
  const queryClient = useQueryClient();

  return useMutation<
    OrderResponse,
    Error,
    string,
    { prevPending?: OrderResponse[]; prevAll?: OrderResponse[] }
  >({
    mutationFn: (orderId: string) => {
      if (!restaurantId) throw new Error('No restaurant');
      return ordersApi.acceptOrder(restaurantId, orderId);
    },
    onMutate: async (orderId) => {
      if (!restaurantId) return {};
      await queryClient.cancelQueries({ queryKey: ordersKeys.byStatus(restaurantId, 'PENDING') });
      const prevPending = queryClient.getQueryData<OrderResponse[]>(
        ordersKeys.byStatus(restaurantId, 'PENDING')
      );
      const prevAll = queryClient.getQueryData<OrderResponse[]>(ordersKeys.all(restaurantId));
      queryClient.setQueryData<OrderResponse[]>(
        ordersKeys.byStatus(restaurantId, 'PENDING'),
        (old) => (old ? old.filter((o) => o.orderId !== orderId) : old)
      );
      return { prevPending, prevAll };
    },
    onError: (err, _orderId, ctx) => {
      if (restaurantId && ctx?.prevPending) {
        queryClient.setQueryData(ordersKeys.byStatus(restaurantId, 'PENDING'), ctx.prevPending);
      }
      if (err instanceof OrderActionError && err.code === 'Conflict') {
        toast.info('Already handled — refreshing');
        if (restaurantId) {
          queryClient.invalidateQueries({ queryKey: ordersKeys.byStatus(restaurantId, 'PENDING') });
          queryClient.invalidateQueries({ queryKey: ordersKeys.all(restaurantId) });
        }
      }
      // payment_capture_failed is intentionally not toasted here — per-row panel renders instead.
    },
    onSuccess: (order) => {
      if (!restaurantId) return;
      queryClient.setQueryData(ordersKeys.detail(restaurantId, order.orderId), order);
      // Write into every cached list variant — different date-range selections
      // each have their own entry, and mutations should fan out to all of them
      // so the optimistic move between columns is visible regardless of which
      // filter the owner has active. Scoped to `listPrefix` so byStatus /
      // byType caches aren't accidentally updated.
      queryClient.setQueriesData<OrderResponse[]>(
        { queryKey: ordersKeys.listPrefix(restaurantId) },
        (old) => (old ? upsertOrder(old, order) : old),
      );
      // Refetch the date-independent active set — its composition changes on
      // every lifecycle transition (accept adds, advance moves, reject/complete
      // remove) so an invalidation is simpler than hand-rolling 4 per-status
      // cache edits.
      queryClient.invalidateQueries({ queryKey: ordersKeys.active(restaurantId) });
    },
  });
}

/**
 * Reject a pending order with an optional reason. Success toast branches on
 * the server's `paymentAction` discriminator (authorization_cancelled /
 * refund_issued / no_payment_action).
 */
export function useRejectOrder(restaurantId: string | undefined) {
  const queryClient = useQueryClient();

  return useMutation<
    OrderResponse,
    Error,
    { orderId: string; reason?: string },
    { prevPending?: OrderResponse[] }
  >({
    mutationFn: ({ orderId, reason }) => {
      if (!restaurantId) throw new Error('No restaurant');
      return ordersApi.rejectOrder(restaurantId, orderId, reason);
    },
    onMutate: async ({ orderId }) => {
      if (!restaurantId) return {};
      await queryClient.cancelQueries({ queryKey: ordersKeys.byStatus(restaurantId, 'PENDING') });
      const prevPending = queryClient.getQueryData<OrderResponse[]>(
        ordersKeys.byStatus(restaurantId, 'PENDING')
      );
      queryClient.setQueryData<OrderResponse[]>(
        ordersKeys.byStatus(restaurantId, 'PENDING'),
        (old) => (old ? old.filter((o) => o.orderId !== orderId) : old)
      );
      return { prevPending };
    },
    onError: (err, _vars, ctx) => {
      if (restaurantId && ctx?.prevPending) {
        queryClient.setQueryData(ordersKeys.byStatus(restaurantId, 'PENDING'), ctx.prevPending);
      }
      if (err instanceof OrderActionError && err.code === 'Conflict') {
        toast.info('Already handled — refreshing');
        if (restaurantId) {
          queryClient.invalidateQueries({ queryKey: ordersKeys.byStatus(restaurantId, 'PENDING') });
          queryClient.invalidateQueries({ queryKey: ordersKeys.all(restaurantId) });
        }
      } else {
        toast.error('Could not cancel order');
      }
    },
    onSuccess: (order) => {
      if (!restaurantId) return;
      queryClient.setQueryData(ordersKeys.detail(restaurantId, order.orderId), order);
      // Write into every cached list variant — different date-range selections
      // each have their own entry, and mutations should fan out to all of them
      // so the optimistic move between columns is visible regardless of which
      // filter the owner has active. Scoped to `listPrefix` so byStatus /
      // byType caches aren't accidentally updated.
      queryClient.setQueriesData<OrderResponse[]>(
        { queryKey: ordersKeys.listPrefix(restaurantId) },
        (old) => (old ? upsertOrder(old, order) : old),
      );
      // Refetch the date-independent active set — its composition changes on
      // every lifecycle transition (accept adds, advance moves, reject/complete
      // remove) so an invalidation is simpler than hand-rolling 4 per-status
      // cache edits.
      queryClient.invalidateQueries({ queryKey: ordersKeys.active(restaurantId) });
      toast.success(rejectSuccessToastCopy(order.paymentAction as RejectPaymentAction | undefined));
    },
  });
}

/**
 * Move an order forward in the lifecycle (PREPARING → IN_DELIVERY / READY_FOR_PICKUP → COMPLETED).
 * 409 Conflict triggers the standard refresh flow.
 */
export function useAdvanceOrder(restaurantId: string | undefined) {
  const queryClient = useQueryClient();

  return useMutation<
    OrderResponse,
    Error,
    { orderId: string; targetStatus: AdvanceTargetStatus }
  >({
    mutationFn: ({ orderId, targetStatus }) => {
      if (!restaurantId) throw new Error('No restaurant');
      return ordersApi.advanceOrder(restaurantId, orderId, targetStatus);
    },
    onError: (err) => {
      if (err instanceof OrderActionError && err.code === 'Conflict') {
        toast.info('Already handled — refreshing');
        if (restaurantId) {
          queryClient.invalidateQueries({ queryKey: ordersKeys.all(restaurantId) });
        }
      } else {
        toast.error('Could not advance order');
      }
    },
    onSuccess: (order) => {
      if (!restaurantId) return;
      queryClient.setQueryData(ordersKeys.detail(restaurantId, order.orderId), order);
      // Write into every cached list variant — different date-range selections
      // each have their own entry, and mutations should fan out to all of them
      // so the optimistic move between columns is visible regardless of which
      // filter the owner has active. Scoped to `listPrefix` so byStatus /
      // byType caches aren't accidentally updated.
      queryClient.setQueriesData<OrderResponse[]>(
        { queryKey: ordersKeys.listPrefix(restaurantId) },
        (old) => (old ? upsertOrder(old, order) : old),
      );
      // Refetch the date-independent active set — its composition changes on
      // every lifecycle transition (accept adds, advance moves, reject/complete
      // remove) so an invalidation is simpler than hand-rolling 4 per-status
      // cache edits.
      queryClient.invalidateQueries({ queryKey: ordersKeys.active(restaurantId) });
    },
  });
}

export function useStartPreparing(restaurantId: string | undefined) {
  const queryClient = useQueryClient();

  return useMutation<
    OrderResponse,
    Error,
    string,
    { prevScheduled?: OrderResponse[] }
  >({
    mutationFn: (orderId: string) => {
      if (!restaurantId) throw new Error('No restaurant');
      return ordersApi.startPreparing(restaurantId, orderId);
    },
    onMutate: async (orderId) => {
      if (!restaurantId) return {};
      await queryClient.cancelQueries({ queryKey: ordersKeys.scheduled(restaurantId) });
      const prevScheduled = queryClient.getQueryData<OrderResponse[]>(
        ordersKeys.scheduled(restaurantId)
      );
      queryClient.setQueryData<OrderResponse[]>(
        ordersKeys.scheduled(restaurantId),
        (old) => (old ? old.filter((o) => o.orderId !== orderId) : old)
      );
      return { prevScheduled };
    },
    onError: (err, _orderId, ctx) => {
      if (restaurantId && ctx?.prevScheduled) {
        queryClient.setQueryData(ordersKeys.scheduled(restaurantId), ctx.prevScheduled);
      }
      if (err instanceof OrderActionError && err.code === 'Conflict') {
        toast.info('Already handled — refreshing');
        if (restaurantId) {
          queryClient.invalidateQueries({ queryKey: ordersKeys.scheduled(restaurantId) });
        }
      } else if (err instanceof OrderActionError && err.status === 402) {
        toast.error('Active subscription required to perform this action');
      } else {
        toast.error('Could not start preparing order');
      }
    },
    onSuccess: (order) => {
      if (!restaurantId) return;
      queryClient.setQueryData(ordersKeys.detail(restaurantId, order.orderId), order);
      queryClient.setQueriesData<OrderResponse[]>(
        { queryKey: ordersKeys.listPrefix(restaurantId) },
        (old) => (old ? upsertOrder(old, order) : old),
      );
      queryClient.invalidateQueries({ queryKey: ordersKeys.active(restaurantId) });
      queryClient.invalidateQueries({ queryKey: ordersKeys.scheduled(restaurantId) });
      toast.success('Order confirmed and sent to kitchen');
    },
  });
}

/**
 * Merge an updated order into the cached "all orders" list. Replaces the
 * existing entry by orderId when present, otherwise prepends it — backends
 * that exclude PENDING from the base list (and only surface them via the
 * status-filtered endpoint) would otherwise drop just-accepted / just-rejected
 * orders instead of moving them into the right column.
 */
function upsertOrder(
  list: OrderResponse[] | undefined,
  order: OrderResponse,
): OrderResponse[] {
  if (!list) return [order];
  const idx = list.findIndex((o) => o.orderId === order.orderId);
  if (idx === -1) return [order, ...list];
  const next = list.slice();
  next[idx] = order;
  return next;
}
